import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: number
  username: string
  email?: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isCheckingAuth: boolean // Track if auth verification is in progress
  rememberMe: boolean
  login: (username: string, password: string, rememberMe?: boolean) => Promise<boolean>
  logout: () => void
  checkAuth: () => Promise<boolean>
}

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isCheckingAuth: false,
      rememberMe: false,

      login: async (username: string, password: string, rememberMe = false) => {
        set({ isLoading: true })
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          })

          if (response.ok) {
            const data = await response.json()
            const user = data.user
            
            // Store auth token in sessionStorage or localStorage based on rememberMe
            const storage = rememberMe ? localStorage : sessionStorage
            storage.setItem('authToken', data.token)
            
            set({ 
              user, 
              isAuthenticated: true, 
              isLoading: false,
              rememberMe 
            })
            
            return true
          } else {
            set({ isLoading: false })
            return false
          }
        } catch (error) {
          console.error('Login error:', error)
          set({ isLoading: false })
          return false
        }
      },

      logout: () => {
        // Clear tokens from both storages
        localStorage.removeItem('authToken')
        sessionStorage.removeItem('authToken')
        
        set({ 
          user: null, 
          isAuthenticated: false, 
          rememberMe: false 
        })
      },

      checkAuth: async () => {
        // Prevent duplicate auth checks
        const { isCheckingAuth } = get()
        if (isCheckingAuth) {
          console.log('⏭️  Skipping duplicate auth verification')
          return false
        }

        // Check for token in both storages
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken')

        if (!token) {
          set({ isAuthenticated: false, user: null })
          return false
        }

        // Mark as checking
        set({ isCheckingAuth: true })

        try {
          console.log('🔐 Verifying authentication...')
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          if (response.ok) {
            const data = await response.json()
            set({
              user: data.user,
              isAuthenticated: true,
              rememberMe: localStorage.getItem('authToken') ? true : false
            })
            console.log('✅ Authentication verified')
            return true
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('authToken')
            sessionStorage.removeItem('authToken')
            set({ isAuthenticated: false, user: null })
            console.log('❌ Authentication failed')
            return false
          }
        } catch (error) {
          console.error('Auth check error:', error)
          set({ isAuthenticated: false, user: null })
          return false
        } finally {
          // Clear checking flag
          set({ isCheckingAuth: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      // Only persist user and rememberMe, not sensitive data
      partialize: (state) => ({ 
        rememberMe: state.rememberMe,
        // Don't persist user data, always verify with server
      }),
    }
  )
)